import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import type { Wedding } from '@/lib/types';
import { cn } from '@/lib/utils';

interface WeddingDetailsFormProps {
  wedding: Wedding | null;
  onUpdate: (data: Partial<Pick<Wedding, 'coupleNames' | 'weddingDate' | 'venue'>>) => void;
}

export function WeddingDetailsForm({ wedding, onUpdate }: WeddingDetailsFormProps) {
  const [coupleNames, setCoupleNames] = useState(wedding?.coupleNames ?? '');
  const [weddingDate, setWeddingDate] = useState(wedding?.weddingDate ?? '');
  const [venue, setVenue] = useState(wedding?.venue ?? '');
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setCoupleNames(wedding?.coupleNames ?? '');
    setWeddingDate(wedding?.weddingDate ?? '');
    setVenue(wedding?.venue ?? '');
  }, [wedding?.id]);

  const handleBlur = (
    field: 'coupleNames' | 'weddingDate' | 'venue',
    value: string
  ) => {
    const original = wedding?.[field] ?? '';
    if (value === original) return;
    onUpdate({ [field]: value || null });
    setSaved((s) => ({ ...s, [field]: true }));
    setTimeout(() => setSaved((s) => ({ ...s, [field]: false })), 1500);
  };

  const inputStyle: React.CSSProperties = {
    background: 'transparent',
    color: 'var(--color-text)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    outline: 'none',
    width: '100%',
    fontSize: 15,
    padding: '8px 0',
    border: 'none',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: 'rgba(255,255,255,0.1)',
    fontFamily: 'var(--font-body)',
  };

  const fields: {
    key: 'coupleNames' | 'weddingDate' | 'venue';
    label: string;
    value: string;
    set: React.Dispatch<React.SetStateAction<string>>;
    type: string;
    placeholder: string;
  }[] = [
    {
      key: 'coupleNames',
      label: 'Couple Names',
      value: coupleNames,
      set: setCoupleNames,
      type: 'text',
      placeholder: 'e.g. Emma & James',
    },
    {
      key: 'weddingDate',
      label: 'Wedding Date',
      value: weddingDate,
      set: setWeddingDate,
      type: 'date',
      placeholder: '',
    },
    {
      key: 'venue',
      label: 'Venue',
      value: venue,
      set: setVenue,
      type: 'text',
      placeholder: 'e.g. The Grand Ballroom',
    },
  ];

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-surface)' }}>
      {fields.map((f, i) => (
        <div
          key={f.key}
          className={cn('px-4 py-3', i < fields.length - 1 && 'border-b')}
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {f.label}
            </label>
            {saved[f.key] && <Check size={12} style={{ color: '#34D399' }} />}
          </div>
          <input
            type={f.type}
            value={f.value}
            placeholder={f.placeholder}
            onChange={(e) => f.set(e.target.value)}
            onBlur={(e) => handleBlur(f.key, e.target.value)}
            style={inputStyle}
          />
        </div>
      ))}
    </div>
  );
}
